import { formatDateISO } from "../../app-input/utils/dateUtils.js";
import { startOfWeek, MONTH_NAMES_DE } from "./periodRange.js";

export function createPeriodPopup(getHostRoot) {
    let popupPortal = null;
    let popupCleanup = null;

    function close() {
        if (popupCleanup) { popupCleanup(); popupCleanup = null; }
        if (popupPortal) { popupPortal.remove(); popupPortal = null; }
    }

    function open(anchorEl, render) {
        close();

        const portal = document.createElement('div');
        portal.style.position = 'fixed';
        portal.style.top = '0';
        portal.style.left = '0';
        portal.style.width = '0';
        portal.style.height = '0';
        portal.style.overflow = 'visible';
        portal.attachShadow({ mode: 'open' });
        const hostRoot = getHostRoot?.();
        if (hostRoot?.adoptedStyleSheets) portal.shadowRoot.adoptedStyleSheets = hostRoot.adoptedStyleSheets;
        document.body.appendChild(portal);

        const panel = document.createElement('div');
        panel.className = 'inputPanel calPanel';
        portal.shadowRoot.appendChild(panel);

        render(panel);

        const rect = anchorEl.getBoundingClientRect();
        const margin = 8;
        panel.style.position = 'fixed';
        panel.style.zIndex = '9999';
        panel.style.display = 'block';
        panel.style.top = `${rect.bottom + 4}px`;
        panel.style.left = `${rect.left}px`;

        const panelWidth = panel.offsetWidth || panel.scrollWidth;
        const panelHeight = panel.offsetHeight || panel.scrollHeight;
        let left = rect.left;
        if (left + panelWidth > window.innerWidth - margin) {
            left = Math.max(margin, window.innerWidth - margin - panelWidth);
        }
        let top = rect.bottom + 4;
        if (top + panelHeight > window.innerHeight - margin) {
            top = Math.max(margin, rect.top - panelHeight - 4);
        }
        panel.style.left = `${left}px`;
        panel.style.top = `${top}px`;

        popupPortal = portal;

        const onOutside = (e) => {
            const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
            if (path.includes(anchorEl) || path.includes(portal)) return;
            close();
        };
        const onKey = (e) => { if (e.key === 'Escape') close(); };
        requestAnimationFrame(() => {
            document.addEventListener('click', onOutside);
            document.addEventListener('keydown', onKey);
        });
        popupCleanup = () => {
            document.removeEventListener('click', onOutside);
            document.removeEventListener('keydown', onKey);
        };
    }

    return { open, close };
}

function buildCalHeaderEl(labelText, onPrev, onNext) {
    const header = document.createElement('div');
    header.className = 'calHeader';

    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'calNavBtn';
    prevBtn.innerHTML = '<i class="fa-solid fa-chevron-left"></i>';
    prevBtn.addEventListener('click', e => { e.stopPropagation(); onPrev(); });

    const label = document.createElement('span');
    label.className = 'calMonthLabel';
    label.textContent = labelText;

    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'calNavBtn';
    nextBtn.innerHTML = '<i class="fa-solid fa-chevron-right"></i>';
    nextBtn.addEventListener('click', e => { e.stopPropagation(); onNext(); });

    header.append(prevBtn, label, nextBtn);
    return header;
}

export function renderWeekPicker(panel, refDate, onSelectWeek) {
    let viewDate = new Date(refDate.getFullYear(), refDate.getMonth(), 1);

    const draw = () => {
        panel.innerHTML = '';
        panel.appendChild(buildCalHeaderEl(
            viewDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
            () => { viewDate = new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1); draw(); },
            () => { viewDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1); draw(); },
        ));
        panel.appendChild(buildWeekGrid(viewDate, refDate, onSelectWeek));
    };
    draw();
}

function buildWeekGrid(viewDate, refDate, onSelectWeek) {
    const grid = document.createElement('div');
    grid.className = 'calGrid';

    ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].forEach(d => {
        const cell = document.createElement('div');
        cell.className = 'calWeekday';
        cell.textContent = d;
        grid.appendChild(cell);
    });

    const month = viewDate.getMonth();
    const lastOfMonth = new Date(viewDate.getFullYear(), month + 1, 0);
    const todayIso = formatDateISO(new Date());
    const selectedMondayIso = formatDateISO(startOfWeek(refDate));

    let cursor = startOfWeek(new Date(viewDate.getFullYear(), month, 1));
    while (cursor <= lastOfMonth) {
        const rowDays = [];
        for (let i = 0; i < 7; i++) {
            rowDays.push(new Date(cursor));
            cursor.setDate(cursor.getDate() + 1);
        }
        const isSelectedRow = formatDateISO(rowDays[0]) === selectedMondayIso;

        const cellEls = rowDays.map((date, i) => {
            const iso = formatDateISO(date);
            const cell = document.createElement('div');
            cell.className = 'calDay';
            cell.textContent = date.getDate();
            if (date.getMonth() !== month) cell.classList.add('calDayOtherMonth');
            if (iso === todayIso) cell.classList.add('calToday');
            cell.classList.add(i === 0 ? 'calWeekPosStart' : i === 6 ? 'calWeekPosEnd' : 'calWeekPosMiddle');
            if (isSelectedRow) cell.classList.add('calWeekActive', 'calWeekSelected');
            return cell;
        });
        cellEls.forEach(cell => {
            cell.addEventListener('mouseenter', () => cellEls.forEach(c => c.classList.add('calWeekActive', 'calWeekHoverBar')));
            cell.addEventListener('mouseleave', () => cellEls.forEach(c => {
                c.classList.remove('calWeekHoverBar');
                if (!isSelectedRow) c.classList.remove('calWeekActive');
            }));
            cell.addEventListener('click', e => { e.stopPropagation(); onSelectWeek(rowDays[0]); });
            grid.appendChild(cell);
        });
    }

    return grid;
}

export function renderMonthPicker(panel, refDate, onSelectMonth) {
    let viewYear = refDate.getFullYear();

    const draw = () => {
        panel.innerHTML = '';
        panel.appendChild(buildCalHeaderEl(
            String(viewYear),
            () => { viewYear -= 1; draw(); },
            () => { viewYear += 1; draw(); },
        ));
        panel.appendChild(buildMonthGrid(viewYear, refDate, onSelectMonth));
    };
    draw();
}

function buildMonthGrid(year, refDate, onSelectMonth) {
    const grid = document.createElement('div');
    grid.className = 'calMonthGrid';

    const selectedYear = refDate.getFullYear();
    const selectedMonth = refDate.getMonth();
    const now = new Date();

    MONTH_NAMES_DE.forEach((name, idx) => {
        const cell = document.createElement('div');
        cell.className = 'calDay calMonthTile';
        cell.textContent = name.slice(0, 3);
        if (year === now.getFullYear() && idx === now.getMonth()) cell.classList.add('calToday');
        if (year === selectedYear && idx === selectedMonth) cell.classList.add('calSelected');
        cell.addEventListener('click', e => { e.stopPropagation(); onSelectMonth(idx); });
        grid.appendChild(cell);
    });

    return grid;
}

export function renderRangePicker(panel, custom, refDate, onSelectRange) {
    let viewDate = custom?.from ? new Date(`${custom.from}T00:00:00`) : new Date(refDate);
    viewDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    let start = custom?.from ?? null;
    let end = custom?.to ?? null;

    const draw = () => {
        panel.innerHTML = '';
        panel.appendChild(buildCalHeaderEl(
            viewDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
            () => { viewDate = new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1); draw(); },
            () => { viewDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1); draw(); },
        ));
        panel.appendChild(buildRangeGrid(viewDate, start, end, (iso) => {
            if (!start || (start && end)) {
                start = iso;
                end = null;
            } else if (iso < start) {
                end = start;
                start = iso;
            } else {
                end = iso;
            }
            if (start && end) {
                onSelectRange(start, end);
                return;
            }
            draw();
        }));

        const footer = document.createElement('div');
        footer.className = 'calFooter';
        const clearBtn = document.createElement('button');
        clearBtn.type = 'button';
        clearBtn.className = 'inputBtn calClearBtn';
        clearBtn.textContent = 'Löschen';
        clearBtn.addEventListener('click', e => {
            e.stopPropagation();
            start = null;
            end = null;
            draw();
        });
        footer.appendChild(clearBtn);
        panel.appendChild(footer);
    };
    draw();
}

function buildRangeGrid(viewDate, start, end, onSelectDay) {
    const grid = document.createElement('div');
    grid.className = 'calGrid';

    ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].forEach(d => {
        const cell = document.createElement('div');
        cell.className = 'calWeekday';
        cell.textContent = d;
        grid.appendChild(cell);
    });

    const year = viewDate.getFullYear(), month = viewDate.getMonth();
    const todayIso = formatDateISO(new Date());

    let firstDay = new Date(year, month, 1).getDay();
    firstDay = firstDay === 0 ? 6 : firstDay - 1;
    for (let i = 0; i < firstDay; i++) {
        const empty = document.createElement('div');
        empty.className = 'calDay calDayEmpty';
        grid.appendChild(empty);
    }

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
        const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const cell = document.createElement('div');
        cell.className = 'calDay';
        cell.textContent = day;
        cell.dataset.date = iso;
        if (iso === todayIso) cell.classList.add('calToday');
        if (iso === start) cell.classList.add('calRangeStart');
        if (iso === end) cell.classList.add('calRangeEnd');
        if (start && end) {
            const lo = start < end ? start : end, hi = start < end ? end : start;
            if (iso > lo && iso < hi) cell.classList.add('calInRange');
        }

        cell.addEventListener('click', e => { e.stopPropagation(); onSelectDay(iso); });

        if (start && !end) {
            cell.addEventListener('mouseenter', () => {
                grid.querySelectorAll('.calDay').forEach(c => c.classList.remove('calInRange', 'calRangeStart', 'calRangeEnd'));
                const lo = start < iso ? start : iso, hi = start < iso ? iso : start;
                grid.querySelectorAll('.calDay[data-date]').forEach(c => {
                    if (c.dataset.date > lo && c.dataset.date < hi) c.classList.add('calInRange');
                });
                grid.querySelector(`.calDay[data-date="${lo}"]`)?.classList.add('calRangeStart');
                grid.querySelector(`.calDay[data-date="${hi}"]`)?.classList.add('calRangeEnd');
            });
        }

        grid.appendChild(cell);
    }

    return grid;
}
